import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { ActivityCompetencyDTO } from 'src/types/dto/competency.dto';
import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { InspectorDTO } from 'src/types/dto/inspector.dto';
import type { IAccreditationBody } from 'src/types/accreditation-body';
import type { IProjectRole } from 'src/types/project-role';
import { AuthorizationLevel } from 'src/types/authorization';

import { getCompetencyName } from './competency';
import { CompetencyHierarchy } from './competency-hierarchy';
import {
  isAuthorizationValidForRange,
  satisfiesLevelRequirement,
} from './project-role-eligibility';

function heldCompetencySatisfiesRequired(
  requiredCompetencyId: number,
  authorization: Pick<AuthorizationDTO, 'competency'>,
  competencyHierarchy: CompetencyHierarchy,
): boolean {
  const heldCompetencyId = authorization.competency?.id;
  if (heldCompetencyId == null) {
    return false;
  }

  return competencyHierarchy.heldCompetencySatisfiesRequired(
    requiredCompetencyId,
    heldCompetencyId,
  );
}

export function buildActivityCompetencyRequirementMaps(
  activityCompetencies: ActivityCompetencyDTO[],
  accreditationBodies?: IAccreditationBody[],
): {
  competencyLevels: Record<number, AuthorizationLevel>;
  competencyAccreditationBodies: Record<number, number>;
} {
  const competencyLevels: Record<number, AuthorizationLevel> = {};
  const competencyAccreditationBodies: Record<number, number> = {};

  const bodyIds = Array.from(
    new Set(
      (accreditationBodies ?? [])
        .map((body) => body.id)
        .filter((id): id is number => id != null),
    ),
  );
  const singleAccreditationBodyId = bodyIds.length === 1 ? bodyIds[0] : undefined;

  for (const requirement of activityCompetencies) {
    const competencyId = requirement.competency?.id;
    if (competencyId == null) {
      continue;
    }

    if (requirement.level != null) {
      competencyLevels[competencyId] = requirement.level;
    }

    if (singleAccreditationBodyId != null) {
      competencyAccreditationBodies[competencyId] = singleAccreditationBodyId;
    }
  }

  return { competencyLevels, competencyAccreditationBodies };
}

export function authorizationSatisfiesCompetencyRequirement(
  authorization: Pick<AuthorizationDTO, 'level' | 'competency' | 'accreditationBody'>,
  competencyId: number,
  requiredLevel: AuthorizationLevel | null | undefined,
  requiredAccreditationBodyId: number | null | undefined,
  projectRoles: IProjectRole[],
  competencyHierarchy: CompetencyHierarchy = CompetencyHierarchy.empty(),
): boolean {
  if (
    !heldCompetencySatisfiesRequired(competencyId, authorization, competencyHierarchy)
  ) {
    return false;
  }

  // Soft AB match (same as backend): null/undefined AB satisfies any required body;
  // only a non-null mismatched AB fails.
  if (
    requiredAccreditationBodyId != null &&
    authorization.accreditationBody != null &&
    authorization.accreditationBody.id !== requiredAccreditationBodyId
  ) {
    return false;
  }

  if (requiredLevel != null) {
    if (
      authorization.level == null ||
      !satisfiesLevelRequirement(authorization.level, requiredLevel, projectRoles)
    ) {
      return false;
    }
  }

  return true;
}

export function isActivityCompetencyCollectivelyCovered(
  activityCompetency: ActivityCompetencyDTO,
  inspectors: InspectorDTO[],
  projectRoles: IProjectRole[],
  rangeStart?: Date,
  rangeEnd?: Date,
  competencyLevels: Record<number, AuthorizationLevel> = {},
  competencyAccreditationBodies: Record<number, number> = {},
  competencyHierarchy: CompetencyHierarchy = CompetencyHierarchy.empty(),
): boolean {
  const competencyId = activityCompetency.competency?.id;
  if (competencyId == null) {
    return true;
  }

  const requiredLevel = competencyLevels[competencyId] ?? activityCompetency.level ?? null;
  const requiredAccreditationBodyId = competencyAccreditationBodies[competencyId];

  return inspectors.some((inspector) => {
    const validAuthorizations = (inspector.authorizations ?? []).filter((auth) =>
      isAuthorizationValidForRange(auth, rangeStart, rangeEnd),
    );

    return validAuthorizations.some((authorization) =>
      authorizationSatisfiesCompetencyRequirement(
        authorization,
        competencyId,
        requiredLevel,
        requiredAccreditationBodyId,
        projectRoles,
        competencyHierarchy,
      ),
    );
  });
}

/**
 * Requirements-panel / allocation-tab highlight for one activity competency.
 * Hierarchy is required so broader held competencies (e.g. Medium/RC3) can
 * light up narrower requirements (e.g. Limited/RC5) the same way save validation does.
 */
export function resolveActivityCompetencyRequirementIsActive({
  activityCompetency,
  inspectors,
  projectRoles,
  rangeStart,
  rangeEnd,
  competencyLevels = {},
  competencyAccreditationBodies = {},
  competencyHierarchy,
  collectivelyCoveredCompetencyIds,
  preferBackendCoverageIds = false,
}: {
  activityCompetency: ActivityCompetencyDTO;
  inspectors: InspectorDTO[];
  projectRoles: IProjectRole[];
  rangeStart?: Date;
  rangeEnd?: Date;
  competencyLevels?: Record<number, AuthorizationLevel>;
  competencyAccreditationBodies?: Record<number, number>;
  competencyHierarchy: CompetencyHierarchy;
  collectivelyCoveredCompetencyIds?: number[];
  preferBackendCoverageIds?: boolean;
}): boolean {
  const competencyId = activityCompetency.competency?.id;
  if (
    preferBackendCoverageIds &&
    competencyId != null &&
    (collectivelyCoveredCompetencyIds?.length ?? 0) > 0
  ) {
    return collectivelyCoveredCompetencyIds!.includes(competencyId);
  }

  return isActivityCompetencyCollectivelyCovered(
    activityCompetency,
    inspectors,
    projectRoles,
    rangeStart,
    rangeEnd,
    competencyLevels,
    competencyAccreditationBodies,
    competencyHierarchy,
  );
}

export function getCollectivelyMissingActivityCompetencies({
  activity,
  inspectors,
  projectRoles,
  competencyHierarchy = CompetencyHierarchy.empty(),
}: {
  activity: ActivityDTO;
  inspectors: InspectorDTO[];
  projectRoles: IProjectRole[];
  competencyHierarchy?: CompetencyHierarchy;
}): ActivityCompetencyDTO[] {
  const requirements = activity.activityCompetencies ?? [];
  if (!requirements.length || !inspectors.length) {
    return requirements;
  }

  const { competencyLevels, competencyAccreditationBodies } =
    buildActivityCompetencyRequirementMaps(
      requirements,
      activity.accreditationBodies,
    );

  const rangeStart = activity.startDate ? new Date(activity.startDate) : undefined;
  const rangeEnd = activity.dueDate ? new Date(activity.dueDate) : undefined;

  return requirements.filter(
    (activityCompetency) =>
      !isActivityCompetencyCollectivelyCovered(
        activityCompetency,
        inspectors,
        projectRoles,
        rangeStart,
        rangeEnd,
        competencyLevels,
        competencyAccreditationBodies,
        competencyHierarchy,
      ),
  );
}

export function getMissingActivityCompetencyLabel(
  activityCompetency: ActivityCompetencyDTO,
  accreditationBodies?: IAccreditationBody[],
  competencyAccreditationBodies: Record<number, number> = {},
): string {
  const competencyLabel = getCompetencyName(activityCompetency.competency);
  const competencyId = activityCompetency.competency?.id;
  const requiredAccreditationBodyId =
    competencyId != null ? competencyAccreditationBodies[competencyId] : undefined;

  if (requiredAccreditationBodyId == null) {
    return competencyLabel;
  }

  const accreditationBodyName = accreditationBodies?.find(
    (body) => body.id === requiredAccreditationBodyId,
  )?.name;

  return accreditationBodyName
    ? `${competencyLabel} - ${accreditationBodyName}`
    : competencyLabel;
}

export function formatMissingActivityCompetenciesMessage(
  missingCompetencies: ActivityCompetencyDTO[],
  accreditationBodies?: IAccreditationBody[],
): string {
  const { competencyAccreditationBodies } = buildActivityCompetencyRequirementMaps(
    missingCompetencies,
    accreditationBodies,
  );

  return missingCompetencies
    .map((activityCompetency) =>
      getMissingActivityCompetencyLabel(
        activityCompetency,
        accreditationBodies,
        competencyAccreditationBodies,
      ),
    )
    .join(', ');
}
